import { useCallback, useEffect, useMemo } from 'react';
import { mutate } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { EngineService } from '@/services/engine';
import {
    getSourceListKey,
    isSourceListPageCacheKey,
} from '@/constants/cache-keys';
import {
    FullSource,
    SourceStatus,
    SourceType,
    SourceVisibility,
} from '@/types/source';

export const SOURCE_LIST_PAGE_SIZE = 12;
const PROCESSING_POLL_INTERVAL_MS = 3000;

const PROCESSING_STATUSES = new Set([
    SourceStatus.PROCESSING,
    SourceStatus.PENDING,
    SourceStatus.QUEUED,
    SourceStatus.FIXING,
    SourceStatus.UPLOADING,
]);

export interface SourceListQuery {
    search?: string;
    visibility: SourceVisibility;
    type?: SourceType;
    orderBy?: string;
}

type SourceListRevalidator = () => Promise<unknown>;

const mountedSourceListRevalidators = new Set<SourceListRevalidator>();

export async function revalidateSourceList(): Promise<void> {
    await mutate(isSourceListPageCacheKey, undefined, { revalidate: false });
    await Promise.all(
        Array.from(mountedSourceListRevalidators, (revalidate) => revalidate()),
    );
}

interface Options {
    enabled?: boolean;
}

export function useSourceListInfinite(
    query: SourceListQuery,
    { enabled = true }: Options = {},
) {
    const keyFn = useMemo(
        () =>
            getSourceListKey(
                {
                    search: query.search ?? '',
                    visibility: query.visibility,
                    type: query.type ?? '',
                    orderBy: query.orderBy ?? '',
                },
                SOURCE_LIST_PAGE_SIZE,
            ),
        [query.search, query.visibility, query.type, query.orderBy],
    );

    const swr = useSWRInfinite(
        enabled ? keyFn : () => null,
        ([, search, visibility, type, orderBy, offset]: readonly [
            string,
            string,
            string,
            string,
            string,
            number,
        ]) =>
            EngineService.getSources({
                page: Math.floor(offset / SOURCE_LIST_PAGE_SIZE) + 1,
                size: SOURCE_LIST_PAGE_SIZE,
                search: search || undefined,
                visibility: visibility as SourceVisibility,
                type: (type || undefined) as SourceType | undefined,
                orderBy: orderBy || undefined,
                includeFolders: true,
            }),
        {
            revalidateAll: false,
            revalidateFirstPage: true,
            revalidateOnFocus: false,
            revalidateOnReconnect: true,
            refreshInterval: (pages) =>
                (pages ?? []).some((page) =>
                    (page.items ?? []).some((source) =>
                        PROCESSING_STATUSES.has(source.status),
                    ),
                )
                    ? PROCESSING_POLL_INTERVAL_MS
                    : 0,
        },
    );

    useEffect(() => {
        mountedSourceListRevalidators.add(swr.mutate);
        return () => {
            mountedSourceListRevalidators.delete(swr.mutate);
        };
    }, [swr.mutate]);

    const sources = useMemo<FullSource[]>(
        () => swr.data?.flatMap((page) => page.items ?? []) ?? [],
        [swr.data],
    );

    const totalSources = swr.data?.[0]?.page?.totalItems ?? 0;

    const hasMore = (() => {
        if (!swr.data || swr.data.length === 0) return false;
        const lastPage = swr.data[swr.data.length - 1];
        if (!lastPage.page) return false;
        return lastPage.page.page < lastPage.page.totalPages;
    })();

    const isLoadingMore =
        Boolean(swr.data) && swr.size > (swr.data?.length ?? 0);

    const loadMore = useCallback(() => {
        if (hasMore && !swr.isValidating) {
            void swr.setSize((previous) => previous + 1);
        }
    }, [hasMore, swr.isValidating, swr.setSize]);

    const resetToFirstPage = useCallback(() => {
        void swr.setSize(1);
    }, [swr.setSize]);

    return {
        sources,
        totalSources,
        hasMore,
        isLoading: swr.isLoading,
        isLoadingMore,
        error: swr.error,
        loadMore,
        resetToFirstPage,
        revalidate: swr.mutate,
    };
}
